import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import type { TokenPair } from "@/types";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

// Token persistence. We keep them in localStorage so sessions survive reloads.
const ACCESS_KEY = "maayad_access";
const REFRESH_KEY = "maayad_refresh";

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set({ access_token, refresh_token }: TokenPair) {
    localStorage.setItem(ACCESS_KEY, access_token);
    localStorage.setItem(REFRESH_KEY, refresh_token);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export const api = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  headers: { "Content-Type": "application/json" },
});

// Attach the access token to every request.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// --- Refresh handling -----------------------------------------------------
// When a 401 is received we try to rotate the refresh token exactly once,
// queueing any concurrent requests so they retry with the fresh token.
let isRefreshing = false;
let queue: ((token: string | null) => void)[] = [];

function flushQueue(token: string | null) {
  queue.forEach((cb) => cb(token));
  queue = [];
}

// Guards against a misrouted deployment (e.g. Nginx serving index.html for an
// /api/* path instead of proxying to the backend): axios only parses JSON
// when Content-Type says so, so a misroute hands call sites an HTML string
// instead of a rejection, silently corrupting whatever state it's assigned to.
function isHtmlResponse(res: { headers: Record<string, unknown>; data: unknown }) {
  const contentType = String(res.headers?.["content-type"] ?? "");
  if (contentType.includes("application/json")) return false;
  return contentType.includes("text/html") || /^\s*<(!doctype|html)/i.test(String(res.data ?? "").slice(0, 100));
}

api.interceptors.response.use(
  (res) => {
    if (isHtmlResponse(res)) {
      return Promise.reject(
        new Error(
          `API at ${res.config?.url} returned HTML instead of JSON — the request likely never reached the backend (check VITE_API_BASE_URL / reverse-proxy routing).`,
        ),
      );
    }
    return res;
  },
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;
    const isAuthCall = original?.url?.includes("/auth/");

    if (status !== 401 || !original || original._retry || isAuthCall) {
      return Promise.reject(error);
    }

    const refresh = tokenStore.refresh;
    if (!refresh) {
      tokenStore.clear();
      return Promise.reject(error);
    }

    original._retry = true;

    if (isRefreshing) {
      // Wait for the in-flight refresh, then retry.
      return new Promise((resolve, reject) => {
        queue.push((token) => {
          if (!token) return reject(error);
          original.headers = original.headers ?? {};
          (original.headers as Record<string, string>).Authorization =
            `Bearer ${token}`;
          resolve(api(original));
        });
      });
    }

    isRefreshing = true;
    try {
      const { data } = await axios.post<TokenPair>(
        `${BASE_URL}/api/v1/auth/refresh`,
        { refresh_token: refresh },
      );
      tokenStore.set(data);
      flushQueue(data.access_token);
      original.headers = original.headers ?? {};
      (original.headers as Record<string, string>).Authorization =
        `Bearer ${data.access_token}`;
      return api(original);
    } catch (refreshError) {
      flushQueue(null);
      tokenStore.clear();
      // Hard redirect to login so stale UI state is cleared.
      if (typeof window !== "undefined") window.location.assign("/login");
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

/** Extract a human-readable message from an Axios error. */
export function errorMessage(err: unknown, fallback = "Something went wrong"): string {
  if (axios.isAxiosError(err)) {
    // No response at all → the backend isn't reachable. This is the most common
    // setup problem, so say so explicitly.
    if (!err.response) {
      return "Can't reach the server. Make sure the backend is running at http://localhost:8000 (run uvicorn in the backend folder).";
    }
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length) {
      // FastAPI validation errors are arrays of {msg, loc}.
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
  }
  return fallback;
}
